<script setup lang="ts">

import { Temporal } from '@js-temporal/polyfill';
import {ChevronDown, Loader2Icon, Pencil} from "lucide-vue-next";
import {Money} from "../../../utils/money.ts";
import {label} from "../../mixins/translations.js";
import {useEnrollmentStore} from "../../stores/enrollment.ts";

const store = useEnrollmentStore();

</script>

<template>
    <div class="rounded-md overflow-hidden" v-if="store.enrollment">
        <div class="flex w-full items-center justify-between bg-white py-4 hidden md:flex">
            <span class="font-bold text-lg text-black">{{ label('site.enrollment.step_3')}}: {{ label('site.enrollment.step_3.header')}}</span>
            <ChevronDown :class="{
                'rotate-180': store.openStep3
            }" class="h-5 w-5 text-primary cursor-pointer" v-if="store.currentStep > 2" @click=" store.openStep3 = !store.openStep3" />
        </div>
        <span class="font-bold md:hidden text-lg text-black" v-if="store.openStep3">{{ label('site.enrollment.step_3')}}: {{ label('site.enrollment.step_3.header')}}</span>

        <div class="bg-gray-100 px-12 py-8" v-if="store.currentStep > 2" v-show="store.openStep3 ">
            <div class="mb-8 ">
                <p class="text-base leading-relaxed text-gray-900" v-text="label('site.enrollment.step_3.title')">
                </p>
                <p class="text-gray-800 text-base leading-relaxed" v-text="label('site.enrollment.step_3.description')">
                </p>
            </div>

            <div class="mb-8" v-if="Object.keys(store.contactDetails).length">
                <div class="mb-3 flex items-center gap-3">
                    <span class="text-2xl font-extrabold text-primary" v-text="label('site.enrollment.contact')"></span>
                    <button @click.prevent="store.moveToStep(1)" type="button" class="text-primary bg-white p-2 rounded-lg hover:bg-gray-200 cursor-pointer">
                        <Pencil class="h-4 w-4" />
                    </button>
                </div>

                <div class="space-y-2 text-gray-900">
                    <div class="grid gap-2 md:grid-cols-2 text-base  text-gray-900">
                        <div class="space-y-1">
                            <p> {{ store.contactDetails.initials }} {{ store.contactDetails.insertion }} {{ store.contactDetails.last_name}} ({{ store.contactDetails.first_name }})</p>
                            <p>{{ store.contactDetails.email }}</p>
                            <p>{{ store.contactDetails.phone }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-8">
                <div class="mb-3 flex items-center gap-3">
                    <span class="text-3xl font-extrabold !text-primary" v-text="label('site.enrollment.students')"></span>
                    <button @click.prevent="store.moveToStep(1)" type="button" class="text-primary bg-white p-2 rounded-lg hover:bg-gray-200 cursor-pointer">
                        <Pencil class="h-4 w-4" />
                    </button>
                </div>

                <div class="space-y-2 text-gray-900 text-base">
                    <div  v-for="(student, index) in store.students" :key="index">
                        {{ student.initials }} {{ student.insertion }} {{ student.last_name}} ({{ student.first_name }}) | {{ student.email }} |
                        {{ student.phone }} | {{ Temporal.PlainDate.from(student.date_of_birth).toLocaleString().replace(/\//g, '-') }}
                    </div>
                </div>
            </div>

            <div class="mb-8">
                <div class="mb-3 flex items-center gap-3">
                    <span class="text-3xl font-extrabold text-primary" v-text="label('site.enrollment.organisation')"></span>
                    <button @click.prevent="store.moveToStep(2)" type="button" class="text-primary bg-white p-2 rounded-lg hover:bg-gray-200 cursor-pointer">
                        <Pencil class="h-4 w-4" />
                    </button>
                </div>

                <div class="grid gap-1 md:grid-cols-2 text-gray-900 text-base ">
                    <div>
                        <p>{{ store.invoiceDetails.organisation }}</p>
                        <p>{{ store.invoiceDetails.street }} {{ store.invoiceDetails.house_number }}</p>
                        <p>{{ store.invoiceDetails.postal }} {{ store.invoiceDetails.city }}</p>
                    </div>

                    <div>
                        <p>{{ store.invoiceDetails.phone }}</p>
                        <p>{{ store.invoiceDetails.email }}</p>
                        <p>{{ store.invoiceDetails.reference }}</p>
                    </div>
                </div>
            </div>

            <div>
                <div class="mb-3 flex items-center gap-3">
                    <span class="text-3xl font-extrabold text-primary" v-text="label('site.enrollment.costs')"></span>
                </div>

                <div class="space-y-2 text-gray-900 text-base">
                    <div>{{ label('site.enrollment.participant_amount') }} <span v-text="store.students.length"></span></div>
                    <p>{{ store.students.length }} × {{ Money.format( store.enrollment.item.price?.price_excl, store.enrollment.item.price.currency, true)  }}</p>
                    <p class="font-semibold">
                        {{ label('site.enrollment.total') }} {{ Money.format(store.totalPrice, store.enrollment.item.price.currency, true) }}
                    </p>
                    <p class="text-sm text-gray-700" v-text="label('site.enrollment.total_disclaimer')">
                    </p>
                </div>
            </div>
        </div>

        <div class="bg-white py-6 flex flex-wrap items-center justify-end gap-4" v-if="store.currentStep > 2" v-show="store.openStep3">
            <div class="text-right">
                <button @click.prevent="store.saveStep3" type="button" class="inline-flex items-center gap-2 rounded-md bg-green-600 px-6 py-3 font-semibold text-base text-white hover:bg-green-700">
                    <Loader2Icon v-if="store.isLoading" class="w-5 h-5 animate-spin " />
                    <span v-else v-text="label('site.enrollment.step_3.button')"></span>
                </button>
                <p class="mt-2 text-sm text-gray-600" v-text="label('site.enrollment.step_3.button.disclaimer')">
                </p>
            </div>
        </div>
    </div>
</template>
